// Tính toán thuần — không DOM, không I/O. An toàn để unit-test.
//
// Chuỗi CM theo định nghĩa của HeliDirect (chốt 02/08/2026):
//   GMV (chưa gồm shipping revenue) − Returns − Promotion = NMV
//   NMV − COGS = Gross Profit
//   GP  − Handling = CM1     [handling = (inbound + outbound − shipping income) + duty + broker]
//   CM1 − MKT      = CM2
//   CM2 − Ecom fee = CM3
//   CM3 − OpEx     = Net Profit
// Mẫu số của MỌI tỷ lệ biên là NMV.
//
// Tháng đã có số thực thì đọc thẳng từ actuals.json; chỉ tháng dự báo mới suy
// ra từ tỷ lệ — và tỷ lệ đó cũng đo từ 5 tháng thực tế.

import {
  GMV_ACT, GMV_FC, RATES, DATA, CURRENT_MONTH, MTD_RATIO, ACTUAL_THROUGH,
} from './constants.js';

const F26 = DATA.fy2026;

/** Tháng `mi` đã có số thực chưa? */
export function hasActual(mi) {
  return mi != null && mi <= ACTUAL_THROUGH;
}

/** Một dòng P&L thực tế của BU tại tháng mi. Null nếu chưa có số. */
export function actualLine(bu, mi, key) {
  if (!hasActual(mi)) return null;
  const s = F26.bu[bu]?.[key];
  return s && s[mi] != null ? s[mi] : null;
}

export function dispGMV(bu, mi) {
  if (mi < CURRENT_MONTH) return { v: GMV_ACT[bu][mi], type: 'act' };
  if (mi === CURRENT_MONTH) {
    const act = GMV_ACT[bu]?.[mi];
    return act != null
      ? { v: act, type: 'mtd' }
      : { v: GMV_FC[bu][mi] * MTD_RATIO, type: 'mtd-fc' };
  }
  return { v: GMV_FC[bu][mi], type: 'fct' };
}

const tyLe = (pl) => {
  const d = pl.nmv;
  return {
    gmPct:  d ? pl.gp / d : null,
    cm1pct: d ? pl.cm1 / d : null,
    cm2pct: d ? pl.cm2 / d : null,
    cm3pct: d ? pl.cm3 / d : null,
    npPct:  d ? pl.np / d : null,
  };
};

/**
 * Chuỗi CM cho một BU.
 * @param {string} bu   CAR | HELI | JET
 * @param {number} gmv  GMV của kỳ (chưa gồm shipping revenue)
 * @param {number} [mi] Chỉ số tháng. Tháng đã có actual → trả về số THỰC.
 */
export function calcPL(bu, gmv, mi = null) {
  if (hasActual(mi)) {
    const g = (k) => actualLine(bu, mi, k) ?? 0;
    const pl = {
      src: 'actual',
      gmv: g('gmv'), ret: g('ret'), promo: g('promo'),
      promoThuc: g('promo_thuc'), wholesale: g('wholesale'), adj: g('adj'),
      nmv: g('nmv'), cogs: g('cogs'), gp: g('gp'),
      inb: g('inb'), ob: g('ob'), si: g('si'), hand: g('hand'),
      cm1: g('cm1'), mkt: g('mkt'), cm2: g('cm2'),
      ecom: g('ecom'), cm3: g('cm3'), opex: g('opex'), np: g('np'),
    };
    return { ...pl, ...tyLe(pl) };
  }

  const r = RATES[bu];
  const ret       = gmv * r.ret;
  const promoThuc = gmv * r.promo_thuc;
  const wholesale = gmv * r.wholesale;
  const promo     = promoThuc + wholesale;
  const nmv       = gmv - ret - promo;
  const cogs      = gmv * r.cogs;
  const gp        = nmv - cogs;
  const inb       = gmv * r.inb;
  const ob        = gmv * r.ob;
  const si        = gmv * r.si;
  const hand      = inb + ob - si;
  const cm1       = gp - hand;
  const mkt       = gmv * r.mkt;
  const cm2       = cm1 - mkt;
  const ecom      = gmv * r.ecom;
  const cm3       = cm2 - ecom;
  const opex      = gmv * r.opex;
  const np        = cm3 - opex;
  const pl = { src: 'forecast', gmv, ret, promo, promoThuc, wholesale, adj: 0,
    nmv, cogs, gp, inb, ob, si, hand, cm1, mkt, cm2, ecom, cm3, opex, np };
  return { ...pl, ...tyLe(pl) };
}

/** Gộp HELI + JET (do một BU manager phụ trách). */
export function calcHJPL(gmv_h, gmv_j, mi = null) {
  const h = calcPL('HELI', gmv_h, mi);
  const j = calcPL('JET', gmv_j, mi);
  const add = (k) => (h[k] || 0) + (j[k] || 0);
  const pl = { src: h.src === 'actual' && j.src === 'actual' ? 'actual' : 'forecast' };
  for (const k of ['gmv','ret','promo','promoThuc','wholesale','adj','nmv','cogs','gp',
                   'inb','ob','si','hand','cm1','mkt','cm2','ecom','cm3','opex','np']) {
    pl[k] = add(k);
  }
  return { ...pl, ...tyLe(pl) };
}

/** P&L cấp công ty tại tháng mi. */
export function companyPL(mi) {
  if (!hasActual(mi)) return null;
  const c = F26.company;
  const at = (k) => (c[k]?.[mi] ?? 0);
  const pl = {};
  for (const k of ['gmv','ret','promo','promo_thuc','wholesale','adj','nmv','cogs','gp',
                   'inb','ob','si','hand','cm1','mkt','cm2','ecom','cm3','opex','np']) {
    pl[k] = at(k);
  }
  pl.promoThuc = pl.promo_thuc;
  return { ...pl, ...tyLe(pl) };
}

// achScore: thực tế so mục tiêu, chặn trần 1.5x. dir='L' = thấp hơn thì tốt hơn.
export function achScore(act, tgt, dir) {
  if (act == null || tgt == null) return null;
  return Math.min(1.5, dir === 'L' ? tgt / act : act / tgt);
}

// weightedScore: điểm KPI có trọng số. Bỏ qua KPI chưa có số.
export function weightedScore(role, getAct) {
  let ws = 0, tw = 0;
  role.kpis.forEach((k, i) => {
    const a = getAct(role.key, i);
    const s = achScore(a, k.tgt, k.dir);
    if (s != null) { ws += s * k.w; tw += k.w; }
  });
  return tw > 0 ? ws / tw : null;
}

// bonusLevel: quy đổi điểm tổng sang mức thưởng.
export function bonusLevel(sc) {
  if (sc == null || sc < 0.8) return { label: 'fail', cls: 'b-D', clsA: 'b-level-D', mult: 0, txt: '< 80% — no bonus' };
  if (sc >= 1.2) return { label: 'vượt xuất sắc', cls: 'b-A', clsA: 'b-level-A', mult: 1.5, txt: '> 120% — ×1.5' };
  if (sc >= 1.0) return { label: 'xuất sắc',     cls: 'b-B', clsA: 'b-level-B', mult: 1.2, txt: '100–120% — ×1.2' };
  return                  { label: 'đạt',         cls: 'b-C', clsA: 'b-level-C', mult: sc,  txt: '80–99% — ×score' };
}

export function achCls(sc) {
  if (sc == null) return '';
  return sc >= 1 ? 'ok' : sc >= 0.9 ? 'near' : 'miss';
}
